import { useNode } from '@craftjs/core';
import { Box, FormControl, FormLabel, Stack, TextField, Select, MenuItem, Button } from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useState, useEffect } from 'react';

interface CSelectBoxOption {
  value: string;
  label: string
}

interface CSelectBoxProps {
  options?: CSelectBoxOption[];
  defaultValue?: string;
  fontSize?: any;
  fontWeight?: any;
  textAlign?: any;
  width?: any;
  height?: any;
  color?: any;
  borderRadius?: any;
  borderColor?: any;
  borderWidth?: any;
}

export const CSelectBox = ({
  options = [],
  defaultValue,
  fontSize,
  fontWeight,
  textAlign,
  color,
  width,
  height,
  borderRadius,
  borderColor,
  borderWidth,
  ...props
}: CSelectBoxProps) => {
  const [currentValue, setCurrentValue] = useState(defaultValue);
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp }
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  useEffect(() => {
    if (selected) {
      return;
    }
  }, [selected]);

  return (
    <Select
      {...props}
      className="craft-selectbox"
      ref={(ref: any) => connect(drag(ref))}

      value={currentValue}
      onChange={(e) => setCurrentValue(e.target.value as string)}
      sx={{ 
        fontSize: `${fontSize}px`, 
        textAlign: textAlign, 
        fontWeight: fontWeight,
        width: `${width} !important`,
        height: `${height} !important`,
        color: color,
        border: selected ? '1px dashed #008522 !important' : '',
        '& fieldset': {
          borderColor: selected ? '#008522' : borderColor,
          borderRadius: borderRadius,
          borderWidth: borderWidth,
        }
      }}
    >
      {options?.map((option: CSelectBoxOption) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const CSelectBoxSettings = () => {
  const {
    actions: { setProp },
    options,
    defaultValue,
    fontSize,
    color,
    textAlign,
    fontWeight,
    width,
    height,
    borderRadius,
    borderColor,
    borderWidth,
  } = useNode((node) => ({
    options: node.data.props.options,
    defaultValue: node.data.props.defaultValue,
    fontSize: node.data.props.fontSize,
    color: node.data.props.color,
    textAlign: node.data.props.textAlign,
    fontWeight: node.data.props.fontWeight,
    width: node.data.props.width,
    height: node.data.props.height,
    borderRadius: node.data.props.borderRadius,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
  }));

  const handleAddOption = () => {
    setProp((props: any) => props.options.push({ value: "", label: "" }));
  };

  const handleRemoveOption = (index: number) => {
    setProp((props: any) => props.options.splice(index, 1));
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    setProp((props: any) => (props.options[index][field] = value));
  };

  return (
    <Stack spacing={2} paddingBottom={4}>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Font Size</FormLabel>
        <TextField
          value={fontSize}
          onChange={(e) =>
            setProp((props: any) => (props.fontSize = e.target.value))
          }
          variant="outlined"
          size="small"
        />
      </FormControl>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Color</FormLabel>
        <MuiColorInput format='hex' value={color} onChange={(color) => {
            setProp((props: any) => (props.color = color), 500);
          }}
        />
      </FormControl>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Width</FormLabel>
          <TextField
            value={width}
            onChange={(e) =>
              setProp((props: any) => (props.width = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Height</FormLabel>
          <TextField
            value={height}
            onChange={(e) =>
              setProp((props: any) => (props.height = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Width</FormLabel>
          <TextField
            value={borderWidth}
            onChange={(e) =>
              setProp((props: any) => (props.borderWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
            setProp((props: any) => (props.borderColor = color), 500);
          }} />
        </FormControl>
      </Stack>
      <FormControl fullWidth={true} margin="normal" component="fieldset">
        <FormLabel component="legend">Border Radius</FormLabel>
        <TextField
          value={borderRadius}
          onChange={(e) =>
            setProp((props: any) => (props.borderRadius = e.target.value))
          }
          variant="outlined"
          size="small"
        />
      </FormControl>
      <Stack paddingTop={4}>
        <Button variant="contained" color="primary" onClick={handleAddOption}>
          Add Option
        </Button>
      </Stack>
      {options?.map((option: CSelectBoxOption, index: number) => (
        <Stack key={index} spacing={1} marginBottom={2} p={1} borderBottom="1px solid #F0F0F0">
          <FormControl fullWidth={true} size="small">
            <FormLabel component="legend">Label</FormLabel>
            <TextField
              value={option.label}
              onChange={(e) =>
                handleInputChange(index, "label", e.target.value)
              }
              variant="outlined"
              size="small"
            />
          </FormControl>
          <FormControl fullWidth={true} size="small">
            <FormLabel component="legend">Value</FormLabel>
            <TextField
              value={option.value}
              onChange={(e) =>
                handleInputChange(index, "value", e.target.value)
              }
              variant="outlined"
              size="small"
            />
          </FormControl>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleRemoveOption(index)}
          >
            Remove
          </Button>
        </Stack>
      ))}
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Default Value</FormLabel>
          <TextField
            value={defaultValue}
            onChange={(e) =>
              setProp((props: any) => (props.defaultValue = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const CSelectBoxDefaultProps = {
  options: [],
  defaultValue: '',
  fontSize: 16,
  color: '#000000',
  textAlign: 'left',
  fontWeight: 300,
  width: '100%',
  height: 'auto',
  borderRadius: '4px',
  borderWidth: '2px',
  borderColor: '#00571f',
};

CSelectBox.craft = {
  displayName: "SelectBox",
  props: CSelectBoxDefaultProps,
  related: {
    settings: CSelectBoxSettings,
  },
};
